import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type DebriefProfileTimerState = 'idle' | 'running' | 'ended';

const STORAGE_KEY = 'debriefProfileFetchTimer';
const DEFAULT_DURATION_SECONDS = 120; // 2 minutes

/**
 * Single app-wide countdown started when the debrief bot ends.
 * The end timestamp is persisted in localStorage so the submit-success page,
 * the dashboard-assessment banner and the my-profile banner all read the
 * exact same timer and stay in sync, even across page reloads.
 */
@Injectable({ providedIn: 'root' })
export class DebriefProfileTimerService {
  readonly state$ = new BehaviorSubject<DebriefProfileTimerState>('idle');
  readonly remainingSeconds$ = new BehaviorSubject<number>(0);

  private durationSeconds = DEFAULT_DURATION_SECONDS;
  private intervalRef: any = null;

  constructor() {
    this.resumeFromStorage();
  }

  get state(): DebriefProfileTimerState {
    return this.state$.value;
  }

  get isRunning(): boolean {
    return this.state === 'running';
  }

  get isEnded(): boolean {
    return this.state === 'ended';
  }

  get remainingSeconds(): number {
    return this.remainingSeconds$.value;
  }

  /** Elapsed progress (0-100) used by the submit-success progress bar. */
  get progressPercent(): number {
    if (this.isEnded) return 100;
    if (!this.isRunning || !this.durationSeconds) return 0;
    const elapsed = this.durationSeconds - this.remainingSeconds;
    return Math.min(100, Math.max(0, (elapsed / this.durationSeconds) * 100));
  }

  /** Remaining time formatted as "HH : MM : SS". */
  get formattedTime(): string {
    const total = Math.max(0, this.remainingSeconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);
    return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
  }

  /** Starts (or restarts) the shared countdown. */
  start(durationSeconds: number = DEFAULT_DURATION_SECONDS): void {
    this.durationSeconds = durationSeconds;
    const endAt = Date.now() + durationSeconds * 1000;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ endAt, durationSeconds }),
    );
    this.remainingSeconds$.next(durationSeconds);
    this.state$.next('running');
    this.beginTicking();
  }

  /** Clears the countdown everywhere (e.g. after the profile status refresh). */
  clear(): void {
    this.stopTicking();
    localStorage.removeItem(STORAGE_KEY);
    this.remainingSeconds$.next(0);
    this.state$.next('idle');
  }

  private resumeFromStorage(): void {
    const stored = this.readStorage();
    if (!stored) return;

    this.durationSeconds = stored.durationSeconds || DEFAULT_DURATION_SECONDS;
    const remaining = Math.max(0, Math.ceil((stored.endAt - Date.now()) / 1000));
    this.remainingSeconds$.next(remaining);

    if (remaining > 0) {
      this.state$.next('running');
      this.beginTicking();
    } else {
      this.state$.next('ended');
    }
  }

  private readStorage(): { endAt: number; durationSeconds: number } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Number.isFinite(parsed?.endAt)) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  private beginTicking(): void {
    this.stopTicking();
    this.intervalRef = setInterval(() => this.tick(), 1000);
  }

  private tick(): void {
    const stored = this.readStorage();
    if (!stored) {
      // Cleared from somewhere else - keep every banner in sync.
      this.stopTicking();
      this.remainingSeconds$.next(0);
      this.state$.next('idle');
      return;
    }

    const remaining = Math.max(0, Math.ceil((stored.endAt - Date.now()) / 1000));
    this.remainingSeconds$.next(remaining);

    if (remaining <= 0) {
      this.stopTicking();
      this.state$.next('ended');
    }
  }

  private stopTicking(): void {
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
      this.intervalRef = null;
    }
  }
}
